/**
 * Template Manager
 * Handles import template creation, detection, and management for recurring imports
 */

import { supabase } from '../supabase-client.js'

/**
 * Generates a column signature from CSV headers
 * Used to detect matching CSV structures
 *
 * @param {string[]} headers - CSV column headers
 * @returns {string} SHA-256 hash of sorted column names
 */
export const generateColumnSignature = async (headers) => {
  if (!headers || headers.length === 0) {
    throw new Error('Headers array cannot be empty')
  }

  // Sort headers alphabetically and join with delimiter
  const normalizedHeaders = headers
    .map(h => h.toLowerCase().trim())
    .sort()
    .join('|')

  // Generate SHA-256 hash using Web Crypto API
  const encoder = new TextEncoder()
  const data = encoder.encode(normalizedHeaders)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

  return hashHex
}

/**
 * Creates a new import template
 *
 * @param {Object} templateData - Template configuration
 * @param {string} templateData.userId - User ID
 * @param {string} templateData.name - Template name
 * @param {string[]} templateData.headers - CSV column headers
 * @param {Object} templateData.columnMappings - CSV column -> Odin field mappings
 * @param {Object} templateData.smartMappings - Customer/branch auto-match rules
 * @param {Object} templateData.productMappings - Product variant -> product ID mappings
 * @returns {Object} Created template
 */
export const createTemplate = async (templateData) => {
  const { userId, name, headers, columnMappings, smartMappings, productMappings } = templateData

  // Generate column signature
  const columnSignature = await generateColumnSignature(headers)

  const { data, error } = await supabase
    .from('import_templates')
    .insert([{
      user_id: userId,
      name: name,
      entity_type: 'sales',
      column_signature: columnSignature,
      column_mappings: columnMappings || {},
      smart_mappings: smartMappings || {},
      product_mappings: productMappings || {}
    }])
    .select()
    .single()

  if (error) {
    // If unique constraint violation, update existing template
    if (error.code === '23505') { // Unique violation
      return await updateTemplateBySignature(columnSignature, {
        name,
        columnMappings,
        smartMappings,
        productMappings,
        userId
      })
    }
    throw error
  }

  return data
}

/**
 * Updates an existing template by column signature
 *
 * @param {string} columnSignature - Column signature hash
 * @param {Object} updates - Fields to update
 * @returns {Object} Updated template
 */
export const updateTemplateBySignature = async (columnSignature, updates) => {
  const { name, columnMappings, smartMappings, productMappings, userId } = updates

  const { data, error } = await supabase
    .from('import_templates')
    .update({
      name: name,
      column_mappings: columnMappings,
      smart_mappings: smartMappings,
      product_mappings: productMappings,
      last_used: new Date().toISOString()
    })
    .eq('column_signature', columnSignature)
    .eq('user_id', userId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Finds a matching template by column signature
 *
 * @param {string[]} headers - CSV column headers
 * @param {string} userId - User ID
 * @returns {Object|null} Matching template or null
 */
export const findMatchingTemplate = async (headers, userId) => {
  const columnSignature = await generateColumnSignature(headers)

  const { data, error } = await supabase
    .from('import_templates')
    .select('*')
    .eq('column_signature', columnSignature)
    .eq('user_id', userId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') { // No rows found
      return null
    }
    throw error
  }

  return data
}

/**
 * Fetches all templates for a user
 *
 * @param {string} userId - User ID
 * @returns {Array} User's templates sorted by last used
 */
export const fetchUserTemplates = async (userId) => {
  const { data, error } = await supabase
    .from('import_templates')
    .select('*')
    .eq('user_id', userId)
    .order('last_used', { ascending: false })

  if (error) throw error
  return data || []
}

/**
 * Updates a template's last_used timestamp
 *
 * @param {string} templateId - Template ID
 * @returns {Object} Updated template
 */
export const updateTemplateLastUsed = async (templateId) => {
  const { data, error } = await supabase
    .from('import_templates')
    .update({ last_used: new Date().toISOString() })
    .eq('id', templateId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Deletes a template
 *
 * @param {string} templateId - Template ID
 * @param {string} userId - User ID (for security)
 * @returns {boolean} Success
 */
export const deleteTemplate = async (templateId, userId) => {
  const { error } = await supabase
    .from('import_templates')
    .delete()
    .eq('id', templateId)
    .eq('user_id', userId)

  if (error) throw error
  return true
}

/**
 * Renames a template
 *
 * @param {string} templateId - Template ID
 * @param {string} newName - New template name
 * @param {string} userId - User ID (for security)
 * @returns {Object} Updated template
 */
export const renameTemplate = async (templateId, newName, userId) => {
  const { data, error } = await supabase
    .from('import_templates')
    .update({ name: newName })
    .eq('id', templateId)
    .eq('user_id', userId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Gets template usage statistics
 *
 * @param {string} templateId - Template ID
 * @returns {Object} Usage stats
 */
export const getTemplateStats = async (templateId) => {
  // Count sessions that used this template
  const { data, error, count } = await supabase
    .from('import_sessions')
    .select('id', { count: 'exact', head: true })
    .eq('template_id', templateId)

  if (error) throw error

  return {
    sessionCount: count || 0
  }
}
